import { QueryFilterOptionKeys, Timestamp } from '@griply/types';

export type TaskStatusFilter = {
  uncompletedOnly?: boolean;
  completedOnly?: boolean;
  unArchivedOnly?: boolean;
  unFrozenOnly?: boolean;
  archivedOnly?: boolean;
};

type StatusFields = {
  completedAt?: Timestamp | null;
  archivedAt?: Timestamp | null;
  frozenAt?: Timestamp | null;
};

// Mirrors the task/habit query's server-side `where` clauses client-side, so
// every status slice derives from one shared subscription instead of a separate
// Firestore listener per status. `!= null` is deliberate (not `!== null`): a doc
// missing the field counts as uncompleted / unarchived / unfrozen, which the
// server `== null` query would never have matched.
const statusFlagPredicates: Record<
  keyof TaskStatusFilter,
  (item: StatusFields) => boolean
> = {
  uncompletedOnly: ({ completedAt }) => completedAt == null,
  completedOnly: ({ completedAt }) => completedAt != null,
  unArchivedOnly: ({ archivedAt }) => archivedAt == null,
  unFrozenOnly: ({ frozenAt }) => frozenAt == null,
  archivedOnly: ({ archivedAt }) => archivedAt != null,
};

const statusFlagKeys = Object.keys(
  statusFlagPredicates,
) as (keyof TaskStatusFilter)[];

export const selectTasksByStatus = <T extends StatusFields>(
  items: T[] | undefined,
  filter: TaskStatusFilter,
): T[] | undefined => {
  if (!items) {
    return items;
  }

  const activeKeys = statusFlagKeys.filter((key) => filter[key]);
  // Same reference when nothing is filtered — downstream memo/`React.memo`
  // boundaries depend on the identity surviving an unfiltered pass.
  if (!activeKeys.length) {
    return items;
  }

  return items.filter((item) =>
    activeKeys.every((key) => statusFlagPredicates[key](item)),
  );
};

// Link filters join the status flags here: the query cannot narrow on the link arrays
// anyway, so `filterTasksByLinks` reproduces them off the base subscription.
const clientDerivableKeys: string[] = [
  ...statusFlagKeys,
  QueryFilterOptionKeys.Goals,
  QueryFilterOptionKeys.LifeAreas,
];

// True when every present option is client-reproducible, i.e. the result can be derived
// from the base subscription instead of a dedicated per-options Firestore listener. A
// server-only option (ids / date windows) makes it non-derivable.
export const isClientDerivableTaskOptions = (options: object): boolean =>
  Object.entries(options).every(
    ([key, value]) => value === undefined || clientDerivableKeys.includes(key),
  );
